import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useNavigate, useParams } from 'react-router-dom'
import { IFsNodeSchema, NodeTypes } from '@cloudike/web_files'
import { allAssetsSelector, selectedAssetsIdsSelector } from 'features/assets/model/selectors/assetsSelectors'
import { assetsActions } from 'features/assets/model/slice/assetsSlice'
import {
  allBoardsSelector, getSystemTagsId,
  selectedBoardsIdsSelector
} from 'features/boards/model/selectors/boardsSelectors'
import {
  boardsActions,
  fetchAllBoardsPublicLinkThunk,
  fetchSystemFieldsThunk
} from 'features/boards/model/slice/boardsSlice'
import { BoardToolbarAction, BoardAssetsToolbarAction } from 'shared/constants/toolbar'
import { useContextMenusManager } from 'features/common/hooks/useContextMenusManager'
import { useOnClickOutsideCards } from 'features/common/hooks/useOnClickOutsideCards'
import { usePressEsc } from 'features/common/hooks/usePressEsc'
import { AUTH_MODAL_MODE, setAuthModalState } from 'features/common/model/slice/appSlice'
import { openModal } from 'features/common/model/slice/moadlSlice'
import { getCurrentFilesPreviewModalStatusSelector, getFilesPreviewItemsSelector } from 'features/files-preview/model/selectors/filesPreviewSelectors'
import { filesPreviewActions, openFilesPreviewThunk } from 'features/files-preview/model/slice/filesPreviewSlice'
import {
  checkAndUploadSharedFilesThunk,
  downloadSharedFilesArchiveThunk,
  fetchFilesPublicLinkBreadcrumbsThunk,
  fetchFilesPublicLinkItems,
  fetchNodeFieldsThunk,
  loadPublicLinkAssetsInBackgroundThunk,
  publicLinkFilesActions,
  updateBoardDescriptionThunk,
  updateBoardNameThunk,
  changePublicBoardTagsThunk,
  changePublicBoardStatusThunk,
  createNewSystemTagThunk
} from 'features/public-link-files/model/slice/publicLinkFilesSlice'
import {
  getCurrentBoardId,
  getFilesPublicLinkBreadcrumbsSelector,
  getFilesPublicLinkLoadingStatusSelector,
  getFilesPublicLinkNodeDataSelector,
  getFilesPublicLinkNodeFieldsSelector,
  getFilesPublicLinkNodesHrefSelector,
  getFilesPublicLinkRootNodeTypeSelector,
  getPath,
  getSystemStatuses,
  getSystemTags,
  getDescriptionFieldId,
  getPublicBoardDescription,
} from 'features/public-link-files/selectors/publicLinkFilesSelectors'
import { isAuthSelector, userSelector } from 'features/user/model/selectors/userSelectors'
import { useAppDispatch, useAppSelector } from 'shared/config/store'
import { LOADING_STATUSES } from 'shared/constants/loadingStatuses'
import { parsePathFromParams } from 'shared/hooks/parsePathFromParams'

import { PUBLIC_LINK_PATHS, PREVIEW_QUERY_PARAMS } from '../constants/publicFiles'
import { UsePublicLinkLogicOptions, UsePublicLinkLogicReturn } from '../types/publicFiles'
import {
  buildPublicLinkPath,
  createEnhancedBreadcrumbs,
  createPermissionFilter,
  getCurrentBoardData,
  isModifierKey,
  processMouseSelection
} from '../utils/publicFilesUtils'

import { useGetEnablePermission } from './useGetEnablePermission'
import { usePublicBoardsAssetsWS } from './usePublicBoardsAssetsWS'

export const usePublicLinkLogic = ({
  isAuthenticatedView = false
}: UsePublicLinkLogicOptions = {}): UsePublicLinkLogicReturn => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()


  const selectedAssetsIds = useAppSelector(selectedAssetsIdsSelector)
  const selectedBoardsIds = useAppSelector(selectedBoardsIdsSelector)
  const loadingStatus = useAppSelector(getFilesPublicLinkLoadingStatusSelector)
  const nodesHref = useAppSelector(getFilesPublicLinkNodesHrefSelector)
  const breadcrumbs = useAppSelector(getFilesPublicLinkBreadcrumbsSelector)
  const rootNodeType = useAppSelector(getFilesPublicLinkRootNodeTypeSelector)
  const nodeData = useAppSelector(getFilesPublicLinkNodeDataSelector)
  const isAuth = useAppSelector(isAuthSelector)
  const nodeFields = useAppSelector(getFilesPublicLinkNodeFieldsSelector)
  const systemStatusOptions = useAppSelector(getSystemStatuses)
  const user = useAppSelector(userSelector)

  const allBoards = useAppSelector(allBoardsSelector)
  const assets = useAppSelector(allAssetsSelector)
  const tagsFieldId = useAppSelector(getSystemTagsId)
  const systemTags = useAppSelector(getSystemTags)
  const boardTags = nodeFields?.tags?.value
  const descriptionFieldId = useAppSelector(getDescriptionFieldId)
  const boardDescription = useAppSelector(getPublicBoardDescription)

  const params = useParams()
  const paths = useAppSelector(getPath)
  const currentBoardId = useAppSelector(getCurrentBoardId)
  const subBoards = useMemo(() => allBoards.filter((item) => (!item.parent_id && breadcrumbs.length === 1) || item.parent_id === paths[paths.length - 1]), [allBoards, paths, breadcrumbs])
  const currentBoardAssets = assets.filter((item) => item.parent_id === currentBoardId)
  const currentPath = buildPublicLinkPath(paths)
  const selectedBoards = useMemo(() => subBoards.filter(board => selectedBoardsIds.includes(board.id)), [subBoards, selectedBoardsIds])
  const selectedAssets = useMemo(() => assets.filter(asset => selectedAssetsIds.includes(asset.id)), [assets, selectedAssetsIds])

  const isPreviewModalOpened = useAppSelector(
    isAuthenticatedView
      ? getCurrentFilesPreviewModalStatusSelector
      : (state) => getFilesPreviewItemsSelector(state).length > 0
  )

  const [withPressedCtrl, setWithPressedCtrl] = useState<boolean>(false)
  const [withPressedShift, setWithPressedShift] = useState<boolean>(false)
  const [isMouseSelecting, setIsMouseSelecting] = useState<boolean>(false)

  const isSubBoardsEmpty = subBoards.length === 0
  const isAssetsEmpty = assets.length === 0
  const isEmptyBoard = isSubBoardsEmpty && isAssetsEmpty
  const isBoardsLoading = loadingStatus === LOADING_STATUSES.LOADING
  const isAssetsLoading = loadingStatus === LOADING_STATUSES.LOADING
  const isPageLoading = isBoardsLoading || isAssetsLoading
  const abortControllerRef = useRef(null)
  const userId = useMemo(() => isAuthenticatedView ? user?.id : 0, [user])

  usePublicBoardsAssetsWS({ currentBoardId })


  const { isDownloadEnabled, isEditContentEnabled, isUploadEnabled } = useGetEnablePermission()

  const { handleContextMenuStateChange, isContextMenuOpen } = useContextMenusManager()

  const currentBoardData = useMemo(() => paths.length > 1 && !breadcrumbs.length ? {
    node_id: paths[paths.length - 1],
    type: 'dir'
  } : getCurrentBoardData(breadcrumbs, nodeData), [breadcrumbs, nodeData, paths])

  const enhancedBreadcrumbs = useMemo(() =>
    createEnhancedBreadcrumbs(breadcrumbs, paths, navigate),
  [breadcrumbs, paths, navigate]
  )

  const filterActionsByPermissions = useMemo(() =>
    createPermissionFilter(isDownloadEnabled, isEditContentEnabled),
  [isDownloadEnabled, isEditContentEnabled]
  )

  // Status UI mapping (mirrors BoardPage)
  const STATUS_MENU_ITEMS = [
    {
      label: 'In progress',
      key: 'In progress',
      color: 'volcano',
    },
    {
      label: 'Waiting for approval',
      key: 'Waiting for approval',
      color: 'blue',
    },
    {
      label: 'Ready to use',
      key: 'Ready to use',
      color: 'green',
    },
    {
      label: 'Remove status',
      key: 'remove-status',
      color: 'red',
    }
  ] as const

  const { boardUIStatus, boardUIStatuses } = useMemo(() => {
    const statusesBase = Array.isArray(systemStatusOptions) ? systemStatusOptions : []

    let uiStatuses = statusesBase.map((status: any) => {
      const statusValue = status?.value
      const uiStatus = STATUS_MENU_ITEMS.find((item) => item.key === statusValue)
      return {
        ...status,
        ...uiStatus,
      }
    })

    // Add "Remove status" action
    uiStatuses.push({
      id: null,
      label: 'Remove status',
      key: 'remove-status',
      color: 'red',
    })

    let currentStatus = null as any
    const currentStatusId = nodeFields?.status?.value
    if (currentStatusId) {
      currentStatus = uiStatuses.find((item: any) => item.id === currentStatusId) || null
    }

    // Adjust list depending on current status
    if (!currentStatus) {
      uiStatuses = uiStatuses.filter((item: any) => item.label !== 'Remove status')
    } else {
      uiStatuses = uiStatuses.filter((item: any) => item.label !== currentStatus.label)
    }

    return { boardUIStatus: currentStatus, boardUIStatuses: uiStatuses }
  }, [systemStatusOptions, nodeFields?.status?.value])

  useEffect(() => {
    const path = parsePathFromParams(params, PUBLIC_LINK_PATHS.PARAM_PREFIX)
    dispatch(publicLinkFilesActions.setPath(path))
  }, [params])

  useEffect(() => {
    dispatch(boardsActions.setCurrentBoardId(currentBoardId))
    dispatch(publicLinkFilesActions.setCurrentBoardId(currentBoardId))
  }, [dispatch, currentBoardId])

  useEffect(() => {
    if (!isPreviewModalOpened) return

    const urlSearchParams = new URLSearchParams(window.location.search)
    const fileId = urlSearchParams.get(PREVIEW_QUERY_PARAMS.FILE_ID)

    if (fileId) {
      dispatch(loadPublicLinkAssetsInBackgroundThunk({
        currentFileId: fileId,
        callback: (items, currentIndex) => {
          dispatch(filesPreviewActions.updatePreviewWithBackgroundData({
            items,
            currentIndex,
            totalItemsCount: items.length
          }))
        }
      }))
    }
  }, [dispatch, isPreviewModalOpened])

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const modifier = isModifierKey(event.key)
      if (modifier === 'ctrl') {
        setWithPressedCtrl(true)
      }
      if (modifier === 'shift') {
        setWithPressedShift(true)
      }
    }

    const handleKeyUp = (event: KeyboardEvent) => {
      const modifier = isModifierKey(event.key)
      if (modifier === 'ctrl') {
        setWithPressedCtrl(false)
      }
      if (modifier === 'shift') {
        setWithPressedShift(false)
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    document.addEventListener('keyup', handleKeyUp)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.removeEventListener('keyup', handleKeyUp)
    }
  }, [])

  useEffect(() => {
    if (loadingStatus === LOADING_STATUSES.SUCCEEDED && nodeData) {
      dispatch(filesPreviewActions.setIsPublicLink(true))

      if (rootNodeType === 'link' || rootNodeType === NodeTypes.FILE) {
        const asset = nodeData

        dispatch(publicLinkFilesActions.setItems([{ ...asset, id: asset.node_id } as any]))
        dispatch(openFilesPreviewThunk({
          items: [{ ...asset, id: asset.node_id } as any],
          currentItemId: asset.node_id,
          totalItemsCount: 1,
          withPreventedClosing: true,
          navigate
        }))
        dispatch(assetsActions.unselectAll())
      }
    }
  }, [dispatch, loadingStatus, navigate, nodeData, rootNodeType])

  useEffect(() => {
    if (!nodesHref) return

    dispatch(fetchFilesPublicLinkItems({ id: currentBoardId }))
    dispatch(boardsActions.unselectAll())
    dispatch(assetsActions.unselectAll())
  }, [dispatch, currentBoardId, nodesHref])

  useEffect(() => {
    if (loadingStatus === LOADING_STATUSES.LOADING) {
      return
    }

    dispatch(boardsActions.unselectAll())
    dispatch(assetsActions.unselectAll())
    dispatch(fetchAllBoardsPublicLinkThunk())
    dispatch(fetchFilesPublicLinkBreadcrumbsThunk({ paths }))
  }, [dispatch, paths, loadingStatus, nodesHref])

  useEffect(() => {
    if (!currentBoardData) return
    dispatch(fetchNodeFieldsThunk({ node: currentBoardData }))
  }, [dispatch, currentBoardData?.node_id, currentBoardData?.id])

  useEffect(() => {
    abortControllerRef.current = new AbortController()
    const { signal } = abortControllerRef.current
    dispatch(fetchSystemFieldsThunk({ signal }))
    return () => {
      abortControllerRef.current?.abort()
      abortControllerRef.current = null
    }
  }, [])

  const unselectAll = useCallback(() => {
    dispatch(boardsActions.unselectAll())
    dispatch(assetsActions.unselectAll())
  }, [dispatch])

  usePressEsc(unselectAll)
  useOnClickOutsideCards(unselectAll)

  const handleSelect = useCallback((item: IFsNodeSchema, isAsset: boolean) => {
    const actions = isAsset ? assetsActions : boardsActions

    if (withPressedCtrl) {
      dispatch(actions.selectItemWithPressedCtrl({ id: item.id }))
    } else if (withPressedShift && selectedAssetsIds.length) {
      dispatch(actions.selectItemWithPressedShift({ id: item.id, items: isAsset ? assets : subBoards }))
    } else if (withPressedShift && !selectedAssetsIds.length) {
      dispatch(actions.selectItem({ id: item.id }))
    } else {
      dispatch(actions.selectItem({ id: item.id }))
    }
  }, [dispatch, withPressedCtrl, withPressedShift, selectedAssetsIds.length, assets, subBoards])

  const handleMouseSelect = useCallback((_: any, result: { id: string, targetClassName: string }[]) => {
    const { selectedAssetsIds, selectedBoardsIds } = processMouseSelection(result, subBoards, assets)

    if (selectedAssetsIds.length || selectedBoardsIds.length) {
      dispatch(assetsActions.setSelectedItemsIds(selectedAssetsIds))
      dispatch(boardsActions.setSelectedItemsIds(selectedBoardsIds))
    }
  }, [dispatch, subBoards, assets])

  const handleMouseSelectEnd = useCallback(() => {
    setIsMouseSelecting(false)
  }, [])

  const handleMouseSelectStart = useCallback(() => {
    setIsMouseSelecting(true)
  }, [])

  const handleSelectAsset = useCallback((asset: IFsNodeSchema) => {
    handleSelect(asset, true)
  }, [handleSelect])

  const handleSelectBoard = useCallback((board: IFsNodeSchema) => {
    handleSelect(board, false)
  }, [handleSelect])

  const handleContextActionClick = useCallback(({
    itemType,
    action,
    itemId,
    items
  }: {
    itemType: any
    action: BoardToolbarAction | BoardAssetsToolbarAction
    itemId: string
    items: IFsNodeSchema[]
  }) => {
    const targetItem = items.find(item => item.id === itemId)
    if (!targetItem) return

    switch (action) {
    case BoardAssetsToolbarAction.Open:
      if (itemType === 'board') {
        navigate(`${currentPath}/${itemId}`)
      }
      if (itemType === 'asset') {
        dispatch(openFilesPreviewThunk({ items: assets as any, currentItemId: itemId, totalItemsCount: 1, navigate }))
        dispatch(assetsActions.unselectAll())
      }
      break

    case BoardAssetsToolbarAction.Download:
      dispatch(downloadSharedFilesArchiveThunk({ items: [targetItem] }))
      break

    case BoardAssetsToolbarAction.Rename:
      dispatch(openModal({ type: BoardToolbarAction.Rename, context: itemType, data: { ids: [targetItem.id], items: [targetItem] } }))
      break

    case BoardAssetsToolbarAction.RemoveFromBoard:
      dispatch(openModal({ type: BoardToolbarAction.RemoveFromBoard, context: itemType, data: { ids: [targetItem.id], items: [targetItem] } }))
      break

    case BoardAssetsToolbarAction.Move:
      dispatch(openModal({ type: BoardToolbarAction.Move, context: itemType, data: { ids: [targetItem.id], items: [targetItem] } }))
      break

    case BoardAssetsToolbarAction.AddToAnotherBoard:
      dispatch(openModal({ type: BoardToolbarAction.AddToAnotherBoard, context: itemType, data: { ids: [targetItem.id], items: [targetItem] } }))
      break
    case BoardToolbarAction.Copy:
      dispatch(openModal({ type: BoardToolbarAction.Copy, context: itemType, data: { ids: [targetItem.id], items: [targetItem] } }))
      break
    }
  }, [dispatch, navigate, currentPath, assets])

  const handleToolbarActionClick = useCallback((
    itemType: any,
    actionId: string,
    items: any[]
  ) => {
    switch (actionId) {
    case BoardAssetsToolbarAction.Open:
      if (items.length === 1) {
        const item = items[0]
        if(item.type === 'dir') {
          navigate(`${currentPath}/${item.id}`)
        }
        if(item.type === 'link') {
          dispatch(openFilesPreviewThunk({ items: assets as any, currentItemId: item.id, totalItemsCount: 1, navigate }))
          dispatch(assetsActions.unselectAll())
        }
      }
      break
    case BoardAssetsToolbarAction.Download:
      dispatch(downloadSharedFilesArchiveThunk({ items }))
      break
    case BoardAssetsToolbarAction.Rename:
      dispatch(openModal({ type: BoardToolbarAction.Rename, context: itemType, data: { ids: [items[0].id], items: [items[0]] } }))
      break
    case BoardAssetsToolbarAction.RemoveFromBoard:
      dispatch(openModal({ type: BoardToolbarAction.RemoveFromBoard, context: itemType, data: { ids: items.map(item => item.id), items: items } }))
      break
    case BoardAssetsToolbarAction.Move:
      dispatch(openModal({ type: BoardToolbarAction.Move, context: itemType, data: { ids: items.map(item => item.id), items: items } }))
      break
    case BoardAssetsToolbarAction.AddToAnotherBoard:
      dispatch(openModal({ type: BoardToolbarAction.AddToAnotherBoard, context: itemType, data: { ids: items.map(item => item.id), items: items } }))
      break
    case BoardToolbarAction.Copy:
      dispatch(openModal({ type: BoardToolbarAction.Copy, context: itemType, data: { ids: items.map(item => item.id), items: items } }))
      break
    }
  }, [dispatch, navigate, currentPath, assets])

  const handleBoardContextActionClick = useCallback((action: BoardToolbarAction, itemId: string) => {
    handleContextActionClick({ itemType: 'board', action, itemId, items: subBoards })
  }, [handleContextActionClick, subBoards])

  const handleAssetsContextActionClick = useCallback((action: BoardAssetsToolbarAction, itemId: string) => {
    handleContextActionClick({ itemType: 'asset', action: action as unknown as BoardToolbarAction, itemId, items: assets })
  }, [handleContextActionClick, assets])

  const handleUploadFiles = useCallback((files: FileList) => {
    dispatch(checkAndUploadSharedFilesThunk({ files }))
  }, [dispatch])

  const handleCloseBoardsToolbar = useCallback(() => {
    dispatch(boardsActions.unselectAll())
  }, [dispatch])

  const handleCloseAssetsToolbar = useCallback(() => {
    dispatch(assetsActions.unselectAll())
  }, [dispatch])

  const handleCloseMixedToolbar = useCallback(() => {
    dispatch(boardsActions.unselectAll())
    dispatch(assetsActions.unselectAll())
  }, [dispatch])

  const openBoard = useCallback((board: IFsNodeSchema) => {
    navigate(`${currentPath}/${board.id}`)
  }, [navigate, currentPath])

  const handleBoardDoubleClick = useCallback((board: IFsNodeSchema) => {
    openBoard(board)
  }, [openBoard])

  const handleAuthClick = useCallback(() => {
    dispatch(setAuthModalState({ isOpen: true, type: 'overlay', mode: AUTH_MODAL_MODE.SIGNIN }))
  }, [dispatch])

  const handleCreateAccountClick = useCallback(() => {
    dispatch(setAuthModalState({ isOpen: true, type: 'overlay', mode: AUTH_MODAL_MODE.SIGNUP }))
  }, [dispatch])

  const handleAssetDoubleClick = useCallback(() => {
    dispatch(assetsActions.unselectAll())
  }, [dispatch])

  const getCurrentNodeId = (nodeData, currentBoardData) => {
    const rootNodeId = nodeData?.node_id
    const currentBoardId = currentBoardData.id
    return currentBoardData.node_id === rootNodeId ? rootNodeId : currentBoardId
  }

  const handleBoardStatusChange = useCallback((status: any) => {
    const shareId = nodeData?.id
    const nodeId = getCurrentNodeId(nodeData, currentBoardData)
    const fieldID = nodeFields?.status?.field?.id
    if (!fieldID) return
    const shouldRemoveStatus = status?.key === 'remove-status'
    const statusId = shouldRemoveStatus ? null : (status?.id ?? status?.value ?? null)

    dispatch(changePublicBoardStatusThunk({ userId, shareId, nodeId: nodeId, fieldID, statusId }))
  }, [dispatch, nodeFields?.status?.field?.id, user?.id, nodeData?.id, nodeData?.node_id, currentBoardData])

  const handleDescriptionChange = useCallback((description: string) => {
    const shareId = nodeData?.id
    const nodeId = getCurrentNodeId(nodeData, currentBoardData)
    if (!shareId || !nodeId) return

    dispatch(updateBoardDescriptionThunk({ userId, nodeId, shareId, fieldID: descriptionFieldId, description }))
  }, [dispatch, currentBoardData, nodeData, descriptionFieldId, userId])

  const handleTitleChange = useCallback((name: string) => {
    const shareId = nodeData?.id
    const nodeId = getCurrentNodeId(nodeData, currentBoardData)

    dispatch(updateBoardNameThunk({ userId, nodeId, shareId, name }))
  }, [dispatch, currentBoardData, nodeData])

  const handleChangeBoardTags = useCallback((ids: string[]) => {
    const shareId = nodeData?.id
    const nodeId = getCurrentNodeId(nodeData, currentBoardData)
    const fieldID = nodeFields?.tags?.field?.id
    if (!fieldID) return
    dispatch(changePublicBoardTagsThunk({ userId, shareId, nodeId, fieldID, data: { value: ids } }))
  }, [dispatch, nodeFields?.tags?.field?.id, user?.id, nodeData?.id, nodeData?.node_id, currentBoardData])

  const handleCreateBoardTag = useCallback((value: string) => {
    abortControllerRef.current = new AbortController()
    const { signal } = abortControllerRef.current.signal
    const nodeId = getCurrentNodeId(nodeData, currentBoardData)
    dispatch(createNewSystemTagThunk({
      currentBoardData,
      fieldID: tagsFieldId,
      systemTags,
      boardTags,
      nodeId,
      newTagValue: value,
      signal
    }))
      .then(() => {
        abortControllerRef.current = null
      })
      .catch(() => {
        abortControllerRef.current = null
      })
  },[tagsFieldId, systemTags, boardTags, nodeData, currentBoardData])

  return {
    subBoards,
    assets: currentBoardAssets,
    selectedAssetsIds,
    selectedBoardsIds,
    selectedBoards,
    selectedAssets,
    breadcrumbs: enhancedBreadcrumbs,
    currentBoardData,
    boardDescription,
    nodeFields,
    currentPath,
    paths,
    isAuth,
    isSingleFileLink: rootNodeType === NodeTypes.FILE || rootNodeType === 'link',
    nodeData,
    withPressedCtrl,
    withPressedShift,
    isMouseSelecting,
    isSubBoardsEmpty,
    isAssetsEmpty,
    isEmptyBoard,
    isPageLoading,
    isPreviewModalOpened,

    isDownloadEnabled,
    isEditContentEnabled,
    isUploadEnabled,
    filterActionsByPermissions,
    boardUIStatus,
    boardUIStatuses,

    handleContextMenuStateChange,
    isContextMenuOpen,

    handleSelect,
    handleMouseSelect,
    handleMouseSelectEnd,
    handleMouseSelectStart,
    handleSelectAsset,
    handleSelectBoard,
    handleContextActionClick,
    handleToolbarActionClick,
    handleBoardContextActionClick,
    handleAssetsContextActionClick,
    handleUploadFiles,
    handleCloseBoardsToolbar,
    handleCloseAssetsToolbar,
    handleCloseMixedToolbar,
    openBoard,
    handleBoardDoubleClick,
    handleAssetDoubleClick,
    handleAuthClick,
    handleCreateAccountClick,
    unselectAll,
    handleBoardStatusChange,
    handleDescriptionChange,
    handleTitleChange,
    handleChangeBoardTags,
    handleCreateBoardTag,
    user
  }
}
